import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Package, TrendingUp, AlertCircle, CheckCircle, Trash2, Factory, Droplets, Scissors, Ship } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductionTrackingPage() {
  const { id } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchTracking();
  }, [id]);

  const fetchTracking = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/production-tracking/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTracking(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch tracking:", err);
      setLoading(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this production entry?")) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/weaving-production/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Entry deleted successfully');
      fetchTracking(); // Refresh
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const getStatusColor = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-800 border-green-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      red: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[color] || colors.green;
  };

  const getStatusIcon = (color) => {
    if (color === 'green') return <CheckCircle size={20} />;
    if (color === 'yellow') return <AlertCircle size={20} />;
    return <AlertCircle size={20} />;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading production tracking...</div>
      </div>
    );
  }

  if (!tracking) {
    return (
      <div className="p-8">
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          Production tracking not available for this PO
        </div>
      </div>
    );
  }

  const weavingStatus = tracking.weaving_status;

  return (
    <div className="p-8">
      <Link to="/purchase-orders" className="inline-flex items-center gap-2 text-primary hover:underline mb-6">
        <ArrowLeft size={18} />
        Back to Purchase Orders
      </Link>

      <div className="mb-8">
        <h1 className="font-heading font-bold text-4xl tracking-tight text-primary mb-2">
          Production Tracking: {tracking.po_number}
        </h1>
        <p className="text-muted-foreground">Real-time production status and WIP tracking</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="text-sm text-muted-foreground mb-1">Current Stage</div>
          <div className="text-2xl font-bold text-primary capitalize">{tracking.current_stage}</div>
        </div>

        <div className="bg-card border border-border rounded-sm p-4">
          <div className="text-sm text-muted-foreground mb-1">Shipment Date</div>
          <div className="text-lg font-bold">
            {tracking.shipment_date ? new Date(tracking.shipment_date).toLocaleDateString() : 'Not set'}
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Pieces</div>
          <div className="text-2xl font-bold">{weavingStatus.total_pieces}</div>
        </div>

        <div className="bg-card border border-border rounded-sm p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Weight</div>
          <div className="text-2xl font-bold">{weavingStatus.total_kg} KG</div>
        </div>
      </div>

      {/* Stage Allocations */}
      {tracking.stage_allocations && (
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <h2 className="font-bold text-xl mb-4">📅 Time Allocation</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded">
              <div className="font-bold text-blue-900">Weaving</div>
              <div className="text-sm text-blue-700">{tracking.stage_allocations.weaving_days} days</div>
              <div className="text-xs text-blue-600 mt-1">
                Deadline: {new Date(tracking.stage_allocations.weaving_deadline).toLocaleDateString()}
              </div>
            </div>
            <div className="p-4 bg-purple-50 border border-purple-200 rounded">
              <div className="font-bold text-purple-900">Dyeing</div>
              <div className="text-sm text-purple-700">{tracking.stage_allocations.dyeing_days} days</div>
              <div className="text-xs text-purple-600 mt-1">
                Deadline: {new Date(tracking.stage_allocations.dyeing_deadline).toLocaleDateString()}
              </div>
            </div>
            <div className="p-4 bg-green-50 border border-green-200 rounded">
              <div className="font-bold text-green-900">Stitching</div>
              <div className="text-sm text-green-700">{tracking.stage_allocations.stitching_days} days</div>
              <div className="text-xs text-green-600 mt-1">
                Deadline: {new Date(tracking.stage_allocations.stitching_deadline).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weaving WIP Status */}
      <div className="bg-card border border-border rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-xl flex items-center gap-2">
            <Package size={24} />
            Weaving Production Status
          </h2>
          <div className={`px-3 py-1 border rounded-full flex items-center gap-2 ${getStatusColor(weavingStatus.status_color)}`}>
            {getStatusIcon(weavingStatus.status_color)}
            <span className="font-semibold text-sm">
              {weavingStatus.is_delayed ? 'DELAYED' : weavingStatus.status_color === 'yellow' ? 'AT RISK' : 'ON TRACK'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center p-4 bg-muted rounded">
            <div className="text-3xl font-bold text-primary">{weavingStatus.produced_pieces}</div>
            <div className="text-sm text-muted-foreground">Pieces Produced</div>
            <div className="text-xs text-muted-foreground mt-1">{weavingStatus.produced_kg} KG</div>
          </div>

          <div className="text-center p-4 bg-muted rounded">
            <div className="text-3xl font-bold text-orange-600">{weavingStatus.balance_pieces}</div>
            <div className="text-sm text-muted-foreground">Balance Remaining</div>
            <div className="text-xs text-muted-foreground mt-1">{weavingStatus.balance_kg} KG</div>
          </div>

          <div className="text-center p-4 bg-muted rounded">
            <div className="text-3xl font-bold text-green-600">{weavingStatus.completion_percentage}%</div>
            <div className="text-sm text-muted-foreground">Completion</div>
            <div className="flex items-center justify-center gap-1 mt-2">
              <TrendingUp size={16} className="text-green-600" />
              <span className="text-xs text-green-600 font-medium">Progress</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
          <div 
            className="bg-primary h-4 rounded-full transition-all duration-300"
            style={{ width: `${weavingStatus.completion_percentage}%` }}
          />
        </div>
        <div className="text-xs text-muted-foreground text-right">
          {weavingStatus.produced_pieces} / {weavingStatus.total_pieces} pieces completed
        </div>
      </div>

      {/* SKU Items */}
      {tracking.sku_items && tracking.sku_items.length > 0 && (
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <h2 className="font-bold text-xl mb-4">📦 SKU Details</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">Size</th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">SKU</th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">Quality</th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">GSM</th>
                  <th className="px-4 py-2 text-right text-xs font-bold uppercase">Pieces</th>
                  <th className="px-4 py-2 text-right text-xs font-bold uppercase">Weight (KG)</th>
                </tr>
              </thead>
              <tbody>
                {tracking.sku_items.map((item, idx) => (
                  <tr key={idx} className="border-b border-border hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium">{item.size}</td>
                    <td className="px-4 py-3 font-mono text-sm">{item.sku}</td>
                    <td className="px-4 py-3">{item.quality}</td>
                    <td className="px-4 py-3">{item.gsm}</td>
                    <td className="px-4 py-3 text-right font-bold">{item.pieces}</td>
                    <td className="px-4 py-3 text-right font-bold">{item.total_kg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Accessories Status */}
      {tracking.accessories_list && tracking.accessories_list.length > 0 && (
        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-bold text-xl mb-4">🎁 Accessories Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tracking.accessories_list.map((acc, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded border ${acc.ordered ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{acc.name}</span>
                  {acc.ordered ? (
                    <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">✓ Ordered</span>
                  ) : (
                    <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">⏳ Pending</span>
                  )}
                </div>
                {acc.linked_po_number && (
                  <div className="text-xs text-muted-foreground mt-1">
                    PO: {acc.linked_po_number}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Production Entries */}
      {weavingStatus.entries && weavingStatus.entries.length > 0 && (
        <div className="bg-card border border-border rounded-sm p-6 mt-6">
          <h2 className="font-bold text-xl mb-4">📝 Daily Production Entries</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">SKU</th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">Size</th>
                  <th className="px-4 py-2 text-right text-xs font-bold uppercase">Pieces</th>
                  <th className="px-4 py-2 text-right text-xs font-bold uppercase">Weight (KG)</th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">Entered By</th>
                </tr>
              </thead>
              <tbody>
                {weavingStatus.entries.map((entry, idx) => (
                  <tr key={idx} className="border-b border-border hover:bg-muted/50">
                    <td className="px-4 py-3">{new Date(entry.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-mono text-sm">{entry.sku}</td>
                    <td className="px-4 py-3">{entry.size}</td>
                    <td className="px-4 py-3 text-right font-bold">{entry.pieces_produced}</td>
                    <td className="px-4 py-3 text-right font-bold">{entry.weight_produced}</td>
                    <td className="px-4 py-3 text-sm">{entry.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
